import React, { useEffect, useState } from 'react';
import secureStorage from '../utils/secureStorage';
import axios from 'axios';
import { AlertCircle, Edit, FileCheck, Plus, Printer, Shield, Trash2, Users, X } from 'lucide-react';
import { Badge } from './ui/badge';
import { Button } from './ui/button';
import SortableEntryList from './SortableEntryList';
import { Checkbox } from './ui/checkbox';
import { API_BASE_URL } from '../config';
import EntryImageCard from './EntryImageCard';
import { writeSanitizedHTML } from '../utils/safePrint';

const API = `${API_BASE_URL}/api`;

const SafetyOfficerModule = ({ theme, exerciseId, exerciseName }) => {
  const [safetyData, setSafetyData] = useState([]);
  const [loading, setLoading] = useState(false);
  const [showForm, setShowForm] = useState(false);
  const [editingEntry, setEditingEntry] = useState(null);
  const [selectedEntries, setSelectedEntries] = useState([]);
  const [activeCategory, setActiveCategory] = useState('incident-details');

  // Helper function to format timestamp in 24-hour format (e.g., "2024-10-27 23:15")
  const format24HourTimestamp = (timestamp) => {
    if (!timestamp) return 'N/A';
    const date = new Date(timestamp);
    const year = date.getFullYear();
    const month = String(date.getMonth() + 1).padStart(2, '0');
    const day = String(date.getDate()).padStart(2, '0');
    const hours = String(date.getHours()).padStart(2, '0');
    const minutes = String(date.getMinutes()).padStart(2, '0');
    return `${year}-${month}-${day} ${hours}:${minutes}`;
  };

  // Form state
  const [formData, setFormData] = useState({
    category: 'incident-details',
    title: '',
    description: '',
    priority: 'medium',
    status: 'active',
    timestamp: '',
    location: '',
    contact_info: '',
    safety_message: '',
    safety_plan: '',
    image: ''
  });

  const [errors, setErrors] = useState({});

  // Categories and their fields
  const categories = {
    'incident-details': {
      label: 'Incident Details',
      icon: AlertCircle,
      fields: [
        { key: 'incident_location_time', label: 'Incident Location', type: 'textarea', required: true, placeholder: 'location of safety issue' },
        { key: 'nature_of_threat', label: 'Nature of the Threat', type: 'textarea', required: true, placeholder: 'the nature or cause of the threat' },
        { key: 'incident_objectives', label: 'Incident Safety Objectives', type: 'textarea', required: true, placeholder: 'your safety objectives for this incident' },
        { key: 'current_planned_actions', label: 'Current and Planned Safety Actions', type: 'textarea', required: true, placeholder: 'list intended safety actions planned for this incident' }
      ]
    },
    'personnel-resources': {
      label: 'Personnel and Resources',
      icon: Users,
      fields: [
        { key: 'personnel_involved', label: 'Personnel Involved', type: 'textarea', required: true, placeholder: 'list individual names, cell phone numbers or number of people involved' },
        { key: 'equipment_status', label: 'Equipment Status and Safety Devices', type: 'textarea', required: true, placeholder: 'list status of equipment, support systems and / or safety devices involved' },
        { key: 'resources_ordered', label: 'Resources Ordered', type: 'textarea', required: true, placeholder: 'list of resources ordered to support safety' }
      ]
    },
    'safety-specific': {
      label: 'Safety-Specific Data',
      icon: Shield,
      fields: [
        { key: 'hazard_assessment', label: 'Hazard Assessments', type: 'textarea', required: true, placeholder: 'assessment of hazardous equipment, materials, chemicals, environment, or incorrect policies and procedures' },
        { key: 'ppe_scba_checks', label: 'PPE and SCBA Checks', type: 'textarea', required: true, placeholder: 'PPE (Personal Protective Equipment) and or SCBA (Self-Contained Breathing Apparatus) worn to protect in hazardous environments' },
        { key: 'ignition_source_checks', label: 'Ignition Source Checks', type: 'textarea', required: true, placeholder: 'identifying potential hazards and confirming safety, which can include assessing and mitigating risks in an industrial setting' },
        { key: 'medical_evacuation_plan', label: 'Medical Evacuation Plan', type: 'textarea', required: true, placeholder: 'procedures that covers the emergency transfer of a patients' },
        { key: 'site_conditions', label: 'Site Conditions (Weather, Lighting, Noise)', type: 'textarea', required: false, placeholder: 'list what systematic physical examinations that were conducted to identify hazards, ensure compliance with regulations, and verify that all safety standards are met' }
      ]
    },
    'findings-recommendations': {
      label: 'Incident Findings',
      icon: FileCheck,
      fields: [
        { key: 'injuries_incidents', label: 'Injuries or Incidents', type: 'textarea', required: false, placeholder: 'list details of safety incident(s), injury(s) or worse' },
        { key: 'physical_evidence', label: 'Physical Evidence', type: 'textarea', required: false, placeholder: 'list findings of facts or evidence that supports this safety incident' },
        { key: 'recommendations', label: 'Recommendations', type: 'textarea', required: true, placeholder: 'list recommendation(s) to prevent this safety incident from happening in the future' }
      ]
    }
  };

  const priorityOptions = [
    { value: 'low', label: 'Low', color: 'text-green-500' },
    { value: 'medium', label: 'Medium', color: 'text-yellow-500' },
    { value: 'high', label: 'High', color: 'text-orange-500' },
    { value: 'critical', label: 'Critical', color: 'text-red-500' }
  ];

  const statusOptions = [
    { value: 'active', label: 'Active', color: 'text-green-500' },
    { value: 'pending', label: 'Pending', color: 'text-yellow-500' },
    { value: 'completed', label: 'Completed', color: 'text-blue-500' },
    { value: 'archived', label: 'Archived', color: 'text-gray-500' }
  ];

  // Load safety data on component mount
  useEffect(() => {
    loadSafetyData();
  }, [exerciseId]);

  const loadSafetyData = async () => {
    if (!exerciseId) return;
    
    try {
      setLoading(true);
      const response = await axios.get(`${API}/safety-officer?exercise_id=${exerciseId}`);
      setSafetyData(response.data);
    } catch (error) {
      console.error('Error loading safety officer data:', error);
    } finally {
      setLoading(false);
    }
  };

  const resetForm = () => {
    setFormData({
      category: activeCategory,
      title: '',
      description: '',
      priority: 'medium',
      status: 'active',
      timestamp: new Date().toISOString().slice(0, 16),
      location: '',
      contact_info: '',
      contact_name: '',
      contact_phone: '',
      safety_message: '',
      safety_plan: '',
      image: ''
    });
    setErrors({});
    setEditingEntry(null);
  };

  const validateForm = () => {
    const newErrors = {};
    const categoryFields = categories[formData.category]?.fields || [];

    if (!formData.title) newErrors.title = 'Title is required';
    if (!formData.description) newErrors.description = 'Description is required';

    // Validate category-specific required fields
    categoryFields.forEach(field => {
      if (field.required && !formData[field.key]) {
        newErrors[field.key] = `${field.label} is required`;
      }
    });

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmitLocal = async (e) => {
    e.preventDefault();
    if (!validateForm()) return;

    try {
      setLoading(true);
      const entryData = {
        ...formData,
        exercise_id: exerciseId,
        timestamp: formData.timestamp || new Date().toISOString()
      };

      if (editingEntry) {
        await axios.put(`${API}/safety-officer/${editingEntry.id}`, entryData);
      } else {
        await axios.post(`${API}/safety-officer`, entryData);
      }

      resetForm();
      setShowForm(false);
      loadSafetyData();
    } catch (error) {
      console.error('Error saving safety officer entry:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleEdit = (entry) => {
    setFormData({
      category: entry.category || 'incident-details',
      title: entry.title || '',
      description: entry.description || '',
      priority: entry.priority || 'medium',
      status: entry.status || 'active',
      timestamp: entry.timestamp ? entry.timestamp.slice(0, 16) : '',
      location: entry.location || '',
      contact_info: entry.contact_info || '',
      contact_name: entry.contact_name || '',
      contact_phone: entry.contact_phone || '',
      safety_message: entry.safety_message || '',
      safety_plan: entry.safety_plan || '',
      image: entry.image || '',
      ...Object.fromEntries(
        Object.keys(categories).flatMap(cat => 
          categories[cat].fields.map(field => [field.key, entry[field.key] || ''])
        )
      )
    });
    setEditingEntry(entry);
    setShowForm(true);
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Are you sure you want to delete this entry?')) return;

    try {
      setLoading(true);
      const token = secureStorage.getToken();
      await axios.delete(`${API}/safety-officer/${id}`, {
        headers: {
          'Authorization': `Bearer ${token}`
        }
      });
      await loadSafetyData();
      alert('Entry deleted successfully');
    } catch (error) {
      console.error('Error deleting safety officer entry:', error);
      if (error.response?.status === 403) {
        alert('You do not have permission to delete this entry. Only Agency Admins and Super Admins can delete entries.');
      } else if (error.response?.status === 404) {
        alert('Entry not found. It may have already been deleted.');
      } else {
        alert('Failed to delete entry. Please try again.');
      }
    } finally {
      setLoading(false);
    }
  };

  const handlePrint = (entry = null) => {
    const printContent = entry ? 
      generateSingleEntryPrint(entry) : 
      generateMultipleEntriesPrint();
    
    const printWindow = window.open('', '_blank');
    writeSanitizedHTML(printWindow.document, `
      <html>
        <head>
          <title>Safety Officer Report</title>
          <style>
            body { font-family: Arial, sans-serif; margin: 20px; }
            .header { text-align: center; margin-bottom: 30px; border-bottom: 2px solid #333; padding-bottom: 10px; }
            .entry { margin-bottom: 30px; border: 1px solid #ddd; padding: 15px; page-break-inside: avoid; }
            .field { margin-bottom: 8px; }
            .label { font-weight: bold; display: inline-block; width: 150px; }
            .category { background: #f5f5f5; padding: 10px; margin-bottom: 15px; border-left: 4px solid #ef4444; }
            .footer { margin-top: 30px; text-align: center; font-size: 12px; color: #666; border-top: 1px solid #ddd; padding-top: 10px; }
            @media print {
              .entry { page-break-inside: avoid; }
            }
          </style>
        </head>
        <body>
          ${printContent}
          <div class="footer">
            <p>Generated on ${new Date().toLocaleString()}</p>
            <p>Powered by EXRSIM - Emergency Response Simulator</p>
          </div>
        </body>
      </html>
    `);
    printWindow.print();
  };

  const generateSingleEntryPrint = (entry) => {
    return `
      <div class="header">
        <h1>Safety Officer Report</h1>
        <h3>Exercise: ${exerciseName || exerciseId}</h3>
      </div>
      ${generateEntryContent(entry)}
    `;
  };

  const generateEntryContent = (entry) => {
    const category = categories[entry.category];
    return `
      <div class="entry">
        <div class="category">
          <h3>${category?.label || entry.category}</h3>
        </div>
        ${entry.image ? `<div class="field"><img src="${entry.image}" alt="Entry image" style="max-width:300px;max-height:200px;border:1px solid #ccc;border-radius:4px;" /></div>` : ''}
        <div class="field"><span class="label">Title:</span> ${entry.title || 'N/A'}</div>
        <div class="field"><span class="label">Description:</span> ${entry.description || 'N/A'}</div>
        <div class="field"><span class="label">Priority:</span> ${entry.priority || 'N/A'}</div>
        <div class="field"><span class="label">Status:</span> ${entry.status || 'N/A'}</div>
        <div class="field"><span class="label">Timestamp:</span> ${entry.timestamp ? format24HourTimestamp(entry.timestamp) : 'N/A'}</div>
        <div class="field"><span class="label">Location:</span> ${entry.location || 'N/A'}</div>
        <div class="field"><span class="label">Contact Name:</span> ${entry.contact_name || 'N/A'}</div>
        <div class="field"><span class="label">Contact Phone:</span> ${entry.contact_phone || 'N/A'}</div>
        <div class="field"><span class="label">Contact Info:</span> ${entry.contact_info || 'N/A'}</div>
        <div class="field"><span class="label">Safety Message:</span> ${entry.safety_message || 'None'}</div>
        <div class="field"><span class="label">Safety Plan:</span> ${entry.safety_plan || 'None'}</div>
        ${category?.fields.map(field =>
          `<div class="field"><span class="label">${field.label}:</span> ${entry[field.key] || 'N/A'}</div>`
        ).join('') || ''}
      </div>
    `;
  };

  const generateMultipleEntriesPrint = () => {
    const selectedData = selectedEntries.length > 0 ? 
      safetyData.filter(entry => selectedEntries.includes(entry.id)) : 
      safetyData;
    
    return `
      <div class="header">
        <h1>Safety Officer Report</h1>
        <h3>Exercise: ${exerciseName || exerciseId}</h3>
        <p>${selectedData.length} entry(ies)</p>
      </div>
      ${selectedData.map(entry => generateEntryContent(entry)).join('')}
    `;
  };

  const toggleEntrySelection = (id) => {
    setSelectedEntries(prev => 
      prev.includes(id) 
        ? prev.filter(entryId => entryId !== id)
        : [...prev, id]
    );
  };

  const filteredData = activeCategory === 'all' 
    ? (safetyData || []) 
    : (safetyData || []).filter(entry => entry.category === activeCategory);

  return (
    <div className="max-w-7xl mx-auto p-6 bg-gray-900 min-h-screen">
      <div className="mb-6">
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-3xl font-bold text-orange-500 mb-2">
              Safety Officer
            </h1>
            <p className="text-white text-sm">
              Manage safety assessments, hazard evaluations, and incident safety protocols
            </p>
          </div>
          <div className="flex space-x-3">
            <Button
              onClick={() => handlePrint()}
              className="bg-blue-500 hover:bg-blue-600 text-white"
            >
              <Printer className="h-4 w-4 mr-2" />
              Print Safety Officer Report
            </Button>
            <Button
              onClick={() => {
                resetForm();
                setShowForm(true);
              }}
              className="bg-blue-500 hover:bg-blue-600 text-white"
            >
              <Plus className="h-4 w-4 mr-2" />
              Add Entry
            </Button>
            {selectedEntries.length > 0 && (
              <Button
                onClick={() => handlePrint()}
                variant="outline"
                className="border-blue-500 text-blue-500 hover:bg-blue-50"
              >
                <Printer className="h-4 w-4 mr-2" />
                Print Selected ({selectedEntries.length})
              </Button>
            )}
          </div>
        </div>
      </div>

      {/* Category Navigation */}
      <div className="mb-6">
        <div className="flex flex-wrap gap-2">
          {Object.entries(categories).map(([key, category]) => {
            const IconComponent = category.icon;
            return (
              <button
                key={key}
                onClick={() => setActiveCategory(key)}
                className={`flex items-center px-4 py-2 rounded-lg transition-colors ${
                  activeCategory === key
                    ? 'bg-orange-500 text-white'
                    : 'bg-gray-700 text-gray-300 hover:bg-gray-600'
                }`}
              >
                <IconComponent className="h-4 w-4 mr-2" />
                {category.label}
              </button>
            );
          })}
          {/* Show All Button */}
          <button
            onClick={() => setActiveCategory('all')}
            className={`flex items-center px-4 py-2 rounded-lg transition-colors ${
              activeCategory === 'all'
                ? 'bg-orange-500 text-white'
                : 'bg-gray-700 text-gray-300 hover:bg-gray-600'
            }`}
          >
            <Shield className={`h-4 w-4 mr-2 ${
              activeCategory === 'all' ? 'text-white' : 'text-red-600'
            }`} />
            <span>Show All</span>
          </button>
        </div>
      </div>

      {/* Entry Form */}
      {showForm && (
        <div className="bg-gray-800 rounded-lg shadow-lg p-6 mb-6 border border-gray-700">
          <div className="flex items-center justify-between mb-4">
            <h2 className="text-xl font-semibold text-orange-500">
              {editingEntry ? 'Edit Entry' : 'Add New Entry'}
            </h2>
            <Button
              onClick={() => {
                resetForm();
                setShowForm(false);
              }}
              variant="ghost"
              size="sm"
            >
              <X className="h-4 w-4" />
            </Button>
          </div>

          <form onSubmit={handleSubmitLocal} className="space-y-6">
            {/* Entry Image Card - for incident-details, personnel-resources, safety-specific */}
            {['incident-details', 'personnel-resources', 'safety-specific'].includes(formData.category) && (
              <EntryImageCard
                value={formData.image}
                onChange={(img) => setFormData({...formData, image: img})}
                title="Entry Image"
              />
            )}

            {/* Title Field */}
            <div>
              <label className="block text-sm font-medium text-white mb-1">
                Title *
              </label>
              <input
                type="text"
                value={formData.title}
                onChange={(e) => setFormData({...formData, title: e.target.value})}
                placeholder="Title of this entry"
                className={`w-full px-3 py-2 border rounded-lg focus:outline-none focus:ring-2 focus:ring-orange-500 bg-gray-700 text-white ${
                  errors.title ? 'border-red-500' : 'border-gray-600'
                }`}
              />
              {errors.title && <p className="text-red-500 text-xs mt-1">{errors.title}</p>}
            </div>

            {/* Emergency Phase Selection */}
            <div>
              <label className="block text-sm font-medium text-white mb-1">
                Emergency Phase *
              </label>
              <select
                value={formData.category}
                onChange={(e) => setFormData({...formData, category: e.target.value})}
                className="w-full px-3 py-2 border border-gray-600 rounded-lg focus:outline-none focus:ring-2 focus:ring-orange-500 bg-gray-700 text-white"
              >
                {Object.entries(categories).map(([key, category]) => (
                  <option key={key} value={key} className={category.color}>{category.label}</option>
                ))}
              </select>
            </div>

            {/* Removed Incident Name and Timestamp fields */}

            {/* Operational Period: Date/Time From and Date/Time To */}
            <div className="grid md:grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium text-white mb-1">
                  Operational Period - Date / Time from:
                </label>
                <div className="grid grid-cols-2 gap-2">
                  <input
                    type="date"
                    value={formData.date_from ? formData.date_from.split('T')[0] : ''}
                    onChange={(e) => {
                      const date = e.target.value;
                      const time = formData.date_from ? formData.date_from.split('T')[1] : '00:00';
                      setFormData({...formData, date_from: `${date}T${time}`});
                    }}
                    className="w-full px-3 py-2 border border-gray-600 rounded-lg focus:outline-none focus:ring-2 focus:ring-orange-500 bg-gray-700 text-white"
                  />
                  <input
                    type="text"
                    placeholder="HH:MM"
                    pattern="[0-2][0-9]:[0-5][0-9]"
                    maxLength="5"
                    value={formData.date_from ? formData.date_from.split('T')[1] : ''}
                    onChange={(e) => {
                      let value = e.target.value.replace(/[^0-9:]/g, '');
                      if (value.length === 2 && !value.includes(':')) {
                        value = value + ':';
                      }
                      if (value.length <= 5) {
                        const date = formData.date_from ? formData.date_from.split('T')[0] : '';
                        setFormData({...formData, date_from: `${date}T${value}`});
                      }
                    }}
                    className="w-full px-3 py-2 border border-gray-600 rounded-lg focus:outline-none focus:ring-2 focus:ring-orange-500 bg-gray-700 text-white"
                  />
                </div>
              </div>
              <div>
                <label className="block text-sm font-medium text-white mb-1">
                  Date / Time to:
                </label>
                <div className="grid grid-cols-2 gap-2">
                  <input
                    type="date"
                    value={formData.date_to ? formData.date_to.split('T')[0] : ''}
                    onChange={(e) => {
                      const date = e.target.value;
                      const time = formData.date_to ? formData.date_to.split('T')[1] : '00:00';
                      setFormData({...formData, date_to: `${date}T${time}`});
                    }}
                    className="w-full px-3 py-2 border border-gray-600 rounded-lg focus:outline-none focus:ring-2 focus:ring-orange-500 bg-gray-700 text-white"
                  />
                  <input
                    type="text"
                    placeholder="HH:MM"
                    pattern="[0-2][0-9]:[0-5][0-9]"
                    maxLength="5"
                    value={formData.date_to ? formData.date_to.split('T')[1] : ''}
                    onChange={(e) => {
                      let value = e.target.value.replace(/[^0-9:]/g, '');
                      if (value.length === 2 && !value.includes(':')) {
                        value = value + ':';
                      }
                      if (value.length <= 5) {
                        const date = formData.date_to ? formData.date_to.split('T')[0] : '';
                        setFormData({...formData, date_to: `${date}T${value}`});
                      }
                    }}
                    className="w-full px-3 py-2 border border-gray-600 rounded-lg focus:outline-none focus:ring-2 focus:ring-orange-500 bg-gray-700 text-white"
                  />
                </div>
              </div>
            </div>

            <div>
              <label className="block text-sm font-medium text-white mb-1">
                Situation Summary *
              </label>
              <textarea
                value={formData.description}
                onChange={(e) => setFormData({...formData, description: e.target.value})}
                rows={3}
                placeholder="a collective description of the current situation"
                className={`w-full px-3 py-2 border rounded-lg focus:outline-none focus:ring-2 focus:ring-orange-500 bg-gray-700 text-white ${
                  errors.description ? 'border-red-500' : 'border-gray-600'
                }`}
              />
              {errors.description && <p className="text-red-500 text-xs mt-1">{errors.description}</p>}
            </div>

            {/* Priority and Status */}
            <div className="grid md:grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium text-white mb-1">
                  Priority
                </label>
                <select
                  value={formData.priority}
                  onChange={(e) => setFormData({...formData, priority: e.target.value})}
                  className="w-full px-3 py-2 border border-gray-600 rounded-lg focus:outline-none focus:ring-2 focus:ring-orange-500 bg-gray-700 text-white"
                >
                  {priorityOptions.map(option => (
                    <option key={option.value} value={option.value}>{option.label}</option>
                  ))}
                </select>
              </div>
              <div>
                <label className="block text-sm font-medium text-white mb-1">
                  Status
                </label>
                <select
                  value={formData.status}
                  onChange={(e) => setFormData({...formData, status: e.target.value})}
                  className="w-full px-3 py-2 border border-gray-600 rounded-lg focus:outline-none focus:ring-2 focus:ring-orange-500 bg-gray-700 text-white"
                >
                  {statusOptions.map(option => (
                    <option key={option.value} value={option.value}>{option.label}</option>
                  ))}
                </select>
              </div>
            </div>

            {/* Contact Name and Contact Phone */}
            <div className="grid md:grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium text-white mb-1">
                  Contact Name
                </label>
                <input
                  type="text"
                  value={formData.contact_name}
                  onChange={(e) => setFormData({...formData, contact_name: e.target.value})}
                  placeholder="First and last name"
                  className="w-full px-3 py-2 border border-gray-600 rounded-lg focus:outline-none focus:ring-2 focus:ring-orange-500 bg-gray-700 text-white"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-white mb-1">
                  Contact Phone
                </label>
                <input
                  type="text"
                  value={formData.contact_phone}
                  onChange={(e) => {
                    // Only allow digits and auto-format as XXX-XXX-XXXX
                    const digits = e.target.value.replace(/\D/g, '').slice(0, 10);
                    let formatted = '';
                    if (digits.length > 0) {
                      formatted = digits.slice(0, 3);
                      if (digits.length > 3) {
                        formatted += '-' + digits.slice(3, 6);
                      }
                      if (digits.length > 6) {
                        formatted += '-' + digits.slice(6, 10);
                      }
                    }
                    if (formatted.length <= 12) {
                      setFormData({...formData, contact_phone: formatted});
                    }
                  }}
                  placeholder="204-123-4567"
                  maxLength="12"
                  pattern="[0-9]{3}-[0-9]{3}-[0-9]{4}"
                  className={`w-full px-3 py-2 border rounded-lg focus:outline-none focus:ring-2 focus:ring-orange-500 bg-gray-700 text-white ${
                    errors.contact_phone ? 'border-red-500' : 'border-gray-600'
                  }`}
                />
                {errors.contact_phone && <p className="text-red-500 text-xs mt-1">{errors.contact_phone}</p>}
              </div>
            </div>

            {/* Category-specific fields */}
            {categories[formData.category]?.fields.map(field => (
              <div key={field.key}>
                <label className="block text-sm font-medium text-white mb-1">
                  {field.label} {field.required && '*'}
                </label>
                {field.type === 'textarea' ? (
                  <textarea
                    value={formData[field.key] || ''}
                    onChange={(e) => setFormData({...formData, [field.key]: e.target.value})}
                    rows={3}
                    placeholder={field.placeholder || ''}
                    className={`w-full px-3 py-2 border rounded-lg focus:outline-none focus:ring-2 focus:ring-orange-500 bg-gray-700 text-white ${
                      errors[field.key] ? 'border-red-500' : 'border-gray-600'
                    }`}
                  />
                ) : (
                  <input
                    type={field.type}
                    value={formData[field.key] || ''}
                    onChange={(e) => setFormData({...formData, [field.key]: e.target.value})}
                    placeholder={field.placeholder || ''}
                    className={`w-full px-3 py-2 border rounded-lg focus:outline-none focus:ring-2 focus:ring-orange-500 bg-gray-700 text-white ${
                      errors[field.key] ? 'border-red-500' : 'border-gray-600'
                    }`}
                  />
                )}
                {errors[field.key] && <p className="text-red-500 text-xs mt-1">{errors[field.key]}</p>}
              </div>
            ))}

            {/* Safety Message */}
            <div>
              <label className="block text-sm font-medium text-white mb-1">
                Safety Message
              </label>
              <textarea
                value={formData.safety_message}
                onChange={(e) => setFormData({...formData, safety_message: e.target.value})}
                rows={3}
                placeholder="key safety message to communicate to incident personnel"
                className="w-full px-3 py-2 border border-gray-600 rounded-lg focus:outline-none focus:ring-2 focus:ring-orange-500 bg-gray-700 text-white"
                data-testid="safety-officer-safety-message"
              />
            </div>

            {/* Safety Plan */}
            <div>
              <label className="block text-sm font-medium text-white mb-1">
                Safety Plan
              </label>
              <textarea
                value={formData.safety_plan}
                onChange={(e) => setFormData({...formData, safety_plan: e.target.value})}
                rows={3}
                placeholder="safety plan / procedures / mitigations"
                className="w-full px-3 py-2 border border-gray-600 rounded-lg focus:outline-none focus:ring-2 focus:ring-orange-500 bg-gray-700 text-white"
                data-testid="safety-officer-safety-plan"
              />
            </div>

            {/* Form Actions */}
            <div className="flex justify-end space-x-3">
              <Button
                type="button"
                onClick={() => {
                  resetForm();
                  setShowForm(false);
                }}
                variant="outline"
                className="border-gray-500 text-gray-500 hover:bg-gray-50"
              >
                Cancel
              </Button>
              <Button
                type="submit"
                disabled={loading}
                className="bg-orange-500 hover:bg-orange-600 text-white"
              >
                {loading ? 'Saving...' : (editingEntry ? 'Update Entry' : 'Add Entry')}
              </Button>
            </div>
          </form>
        </div>
      )}

      {/* Entries List */}
      <div className="bg-gray-800 rounded-lg shadow-lg border border-gray-700">
        <div className="p-6 border-b border-gray-700">
          <h3 className="text-lg font-semibold text-orange-500">
            {activeCategory === 'all' ? 'All Entries' : categories[activeCategory]?.label + ' Entries'}
          </h3>
        </div>
        
        <div className="p-6">
          {loading ? (
            <div className="text-center py-8">
              <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-orange-500 mx-auto"></div>
              <p className="text-white mt-2">Loading entries...</p>
            </div>
          ) : filteredData.length === 0 ? (
            <div className="text-center py-8">
              <Shield className="h-16 w-16 text-gray-400 mx-auto mb-4" />
              <h3 className="text-lg font-medium text-orange-500 mb-2">
                {activeCategory === 'all' ? 'No Safety Entries Found' : `No ${categories[activeCategory]?.label} Entries`}
              </h3>
              <p className="text-white mb-4">
                {activeCategory === 'all' 
                  ? 'Start by adding your first safety entry in any category for this exercise.'
                  : `Start by adding your first ${categories[activeCategory]?.label.toLowerCase()} entry for this exercise.`
                }
              </p>
            </div>
          ) : (
            <SortableEntryList
              entries={filteredData}
              setEntries={(newEntries) => {
                if (activeCategory === 'all') {
                  setSafetyData(newEntries);
                } else {
                  const otherEntries = safetyData.filter(d => d.category !== activeCategory);
                  setSafetyData([...otherEntries, ...newEntries]);
                }
              }}
              reorderEndpoint="safety-officer/reorder"
              renderEntry={(entry) => {
                const priorityOption = priorityOptions.find(p => p.value === entry.priority);
                const statusOption = statusOptions.find(s => s.value === entry.status);
                
                return (
                  <div className="bg-gray-700 rounded-lg p-4 border border-gray-600">
                    <div className="flex items-start justify-between">
                      <div className="flex-1">
                        <div className="flex items-center space-x-3 mb-2">
                          <Checkbox
                            checked={selectedEntries.includes(entry.id)}
                            onCheckedChange={() => toggleEntrySelection(entry.id)}
                          />
                          {entry.image && (
                            <img
                              src={entry.image}
                              alt="Entry"
                              className="w-[60px] h-[60px] rounded object-cover border border-gray-500 flex-shrink-0"
                              data-testid={`safety-entry-image-${entry.id}`}
                            />
                          )}
                          <h4 className="text-lg font-semibold text-white">{entry.title}</h4>
                          <Badge className={`${priorityOption?.color} bg-transparent border`}>
                            {priorityOption?.label}
                          </Badge>
                          <Badge className={`${statusOption?.color} bg-transparent border`}>
                            {statusOption?.label}
                          </Badge>
                        </div>
                        
                        <p className="text-gray-300 mb-3">{entry.description}</p>
                        
                        <div className="grid md:grid-cols-2 gap-4 text-sm text-gray-400">
                          {entry.timestamp && (
                            <div>
                              <span className="font-medium">Timestamp:</span> {format24HourTimestamp(entry.timestamp)}
                            </div>
                          )}
                          {entry.location && (
                            <div>
                              <span className="font-medium">Location:</span> {entry.location}
                            </div>
                          )}
                          {entry.contact_info && (
                            <div>
                              <span className="font-medium">Contact:</span> {entry.contact_info}
                            </div>
                          )}
                        </div>
                        
                        {entry.safety_message && (
                          <div className="mt-3 p-3 bg-gray-600 rounded text-sm text-gray-300">
                            <span className="font-medium">Safety Message:</span> {entry.safety_message}
                          </div>
                        )}
                        {entry.safety_plan && (
                          <div className="mt-2 p-3 bg-gray-600 rounded text-sm text-gray-300">
                            <span className="font-medium">Safety Plan:</span> {entry.safety_plan}
                          </div>
                        )}
                      </div>
                      
                      <div className="flex space-x-2 ml-4">
                        <Button
                          onClick={() => handlePrint(entry)}
                          variant="ghost"
                          size="sm"
                          className="text-blue-400 hover:text-blue-300"
                        >
                          <Printer className="h-4 w-4" />
                        </Button>
                        <Button
                          onClick={() => handleEdit(entry)}
                          variant="ghost"
                          size="sm"
                          className="text-orange-400 hover:text-orange-300"
                        >
                          <Edit className="h-4 w-4" />
                        </Button>
                        <Button
                          onClick={() => handleDelete(entry.id)}
                          variant="ghost"
                          size="sm"
                          className="text-red-400 hover:text-red-300"
                        >
                          <Trash2 className="h-4 w-4" />
                        </Button>
                      </div>
                    </div>
                  </div>
                );
              }}
            />
          )}
        </div>
      </div>
    </div>
  );
};

// Situation Unit Module Component

export default SafetyOfficerModule;
